<template>
  <q-card class="cmp-properties-summary ws-card q-pa-lg">
    <h6 class="q-mb-md">
      {{ $t(title) }}
    </h6>
    <q-skeleton v-if="loading" type="QRange" animation="fade" height="100px" />
    <template v-else>
      <div class="item text-ws-base110 q-pa-md">
        <div class="title-wrapper relative-position">
          <price-period
            class="float-right q-ml-md"
            :period-under="!!$slots.info"
            :prices="info.prices"
            :price="info.price"
            :period="period"
          />
          <small class="text-bold text-ws-base140">{{
            info.title ? $t(info.title) : info.brand
          }}</small>
        </div>
        <div v-if="$slots.info" class="secondary q-mt-sm">
          <slot name="info"></slot>
          <div v-if="$slots['info-secondary']">
            <slot name="info-secondary"></slot>
          </div>
        </div>
      </div>

      <ws-list
        v-if="items.length"
        class="q-ml-xs q-mt-md q-mb-xl"
        :items="items"
      >
        <template v-slot="scope">
          <small class="q-pl-sm inline-block full-width">
            <price-period
              v-if="!simpleItems"
              class="float-right q-ml-md"
              :price="scope.item.i_price"
              individual
              :period="period"
            />
            {{ getItemLabel(scope) }}
          </small>
        </template>
      </ws-list>
      <slot v-else name="empty"></slot>
    </template>

    <hr class="ws-default q-mt-xl q-mb-md" />
    <table class="price-table" cellspacing="0" cellpadding="0" width="100%">
      <tfoot class="total">
        <tr>
          <td class="text">
            <small>{{ $t('summary.sum') }}:</small>
          </td>
          <td class="price text-right">
            <price-period individual :price="totalPrice" :period="period" />
          </td>
        </tr>
      </tfoot>
    </table>

    <slot></slot>
  </q-card>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { QCard, QCardSection, QIcon, QSkeleton } from 'quasar';

import { getSnakedKeyFactory } from '@odin/utils';
import { isNumber } from 'lodash-es';
import WsList from '@/components/WsList.vue';
import {WsIcon} from '@odin/components-vue2';
import { type CartItemInfo } from '@/store/const';
import PricePeriod from '@/components/PricePeriod.vue';
import { resolvePrice } from '@/utilities';
import type { ProductPrice } from '@/types';
import { useI18n } from '@odin/services';

@Component({
  components: {
    QCard,
    QCardSection,
    QIcon,
    QSkeleton,
    WsList,
    WsIcon,
    PricePeriod,
  },
})
export default class PropertiesSummary extends Vue {
  @Prop({ required: true })
  readonly properties!: string[];
  @Prop()
  readonly info!: CartItemInfo;
  @Prop({ type: String, default: 'product.properties.summary.title' })
  readonly title!: string;
  @Prop({ default: () => ({}) })
  readonly model!: any;
  @Prop({ default: 'product.properties' })
  readonly path!: string;
  @Prop({ default: 0 })
  readonly additionalPrice!: number;
  @Prop({ default: null })
  readonly defaultPrice!: number;
  @Prop(Boolean)
  readonly simpleItems!: boolean;

  i18next = useI18n();
  $t = this.i18next.t;

  get loading() {
    return !this.info || !this.properties;
  }

  get period() {
    return this.info?.period ?? this.model?.period;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  get items() {
    if (this.loading) {
      return [];
    }

    const items: any[] = [];
    const model = this.model;
    const period = this.period;

    this.properties.forEach(property => {
      const propertyModel = model[property];

      if (!property || (!propertyModel && !this.simpleItems)) {
        return;
      }

      /* PRICE */
      let price = resolvePrice(propertyModel?.prices, period);
      // fallback to price
      if (price === undefined) {
        price = resolvePrice(propertyModel?.price, period);
      }

      /* TRANSLATION */
      const tKeys = [property];
      if (propertyModel?.type) {
        tKeys.push(propertyModel.type);
      }
      const mainKey = this.getKey(...tKeys, 'label');

      /* WRAPPING */
      const item = {
        i_label: mainKey,
        i_label_values: {
          ...propertyModel,
        },
        i_price: price ?? this.defaultPrice,
        ...propertyModel,
      };

      /* EXCEPTION FOR OS LICENCES HANDLING */
      if (property === 'os') {
        item.i_label = propertyModel?.name;
        if (model.licences?.length) {
          item.i_price += model.licences.reduce(
            (acc: number, licence: any) =>
              (acc += resolvePrice(licence.prices, period) || 0),
            0
          );
        } else {
          item.i_price = price ?? 0;
        }
      }

      /* EXCEPTION FOR VOLUMETRICAL PROPERTIES */
      if (item.i_price && propertyModel.volume !== undefined) {
        // multiply by volume
        item.i_price *= propertyModel.volume;
      }

      items.push(item);
    });

    return items;
  }

  get totalPrice() {
    if (this.loading) {
      return null;
    }
    let total = this.items.reduce((acc: number | null, item: any) => {
      if (acc === null || item.i_price === null) {
        acc = null;
      } else {
        acc += item.i_price;
      }
      return acc;
    }, (resolvePrice(this.info?.prices || (this.info?.price as ProductPrice), this.period) as any) || 0);
    if (this.additionalPrice && isNumber(total)) {
      total += this.additionalPrice;
    }
    return total;
  }

  getKey(...props: string[]): string {
    this.getKey = getSnakedKeyFactory(this.path);
    return this.getKey(...props);
  }

  getItemLabel(scope: any) {
    if (scope.item.i_label.startsWith('product.properties.snapshot_slot')) {
      return this.$t( this.path + '.snapshot_slot.label', {count:  Number.parseInt(scope.item.i_label_values.value)});
    }

    return this.i18next.i18next.exists(scope.item.i_label)
      ? this.$t(scope.item.i_label, {...scope.item.i_label_values.value})
      : scope.item.i_label;
  }

}
</script>

<style lang="scss" scoped>
.cmp-properties-summary.ws-card.q-card {
  border: $ws-border-thick;
  border-radius: $ws-border-radius;
  box-shadow: $ws-box-shadow7;

  ::v-deep ul li:last-of-type > .dot:before {
    content: none;
  }

  .title-wrapper {
    @include ws-typo-small;
  }

  .item {
    border: $ws-border-thick;
    border-radius: $ws-border-radius;
    border-width: 1px;
    border-color: $ws-base090;

    .secondary {
      @include ws-typo-xs;
    }
  }

  hr.ws-default {
    border-color: $ws-base090;
  }

  table.price-table {
    border-collapse: collapse;
    table-layout: auto;

    > tfoot.total {
      table-layout: auto;
      color: $ws-base110;

      .price > .cmp-price-period::v-deep {
        .price > strong {
          @include ws-typo-heading6;
        }
        .period {
          color: $ws-base110;
        }
      }
    }
  }

  @media (max-width: $ws-breakpoint-mobile-max) {
    border: none;
    .title {
      display: none;
    }
  }
}
</style>
